import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { createSettlementApi, listAccountsApi, uploadFinFileApi } from "../api/financeiroApi";
import { FinAccount, FinEntry, FinPaymentMethod, PAYMENT_METHOD_LABELS } from "../api/finTypes";
import { formatCurrency, formatDateOnly } from "../../dp/utils/format";
import { parseMoneyInput, todayYmd } from "../utils/fin";
import "../../dp/pages/Dp.css";

type Props = {
  token: string;
  entry: FinEntry;
  onClose: () => void;
  /** Recebe o título já atualizado pelo backend. */
  onSaved: (entry: FinEntry) => void;
};

const METHODS = Object.keys(PAYMENT_METHOD_LABELS) as FinPaymentMethod[];

/**
 * Baixa de um título: total (padrão) ou parcial, com comprovante opcional.
 * O comprovante sobe antes, por `POST /financeiro/files`, e a baixa leva o assetId.
 */
export default function SettlementModal({ token, entry, onClose, onSaved }: Props) {
  const isReceivable = entry.type === "RECEBER";
  const verb = isReceivable ? "Receber" : "Pagar";

  const [accounts, setAccounts] = useState<FinAccount[] | null>(null);
  const [accountId, setAccountId] = useState(entry.suggestedAccountId ?? "");
  const [paidAt, setPaidAt] = useState(todayYmd());
  const [amount, setAmount] = useState(entry.openAmount);
  const [method, setMethod] = useState<FinPaymentMethod>("PIX");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listAccountsApi(token)
      .then((list) => {
        setAccounts(list);
        // Sem conta sugerida (ou sugerida inativa), e só uma conta: já vem escolhida.
        setAccountId((current) =>
          current && list.some((a) => a.id === current) ? current : list.length === 1 ? list[0].id : ""
        );
      })
      .catch((error: Error) => {
        setAccounts([]);
        toast.error(error.message);
      });
  }, [token]);

  const value = parseMoneyInput(amount);
  const open = Number(entry.openAmount);
  const errors = useMemo(() => {
    const list: string[] = [];
    if (!accountId) list.push("Escolha a conta.");
    if (!paidAt) list.push("Informe a data.");
    else if (paidAt > todayYmd()) list.push("A data não pode ser futura.");
    if (!(value > 0)) list.push("Informe o valor.");
    else if (value > open) list.push(`O valor passa do saldo em aberto (${formatCurrency(open)}).`);
    return list;
  }, [accountId, paidAt, value, open]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (errors.length) return toast.error(errors[0]);

    setSaving(true);
    try {
      const uploaded = file ? await uploadFinFileApi(token, file) : null;
      const updated = await createSettlementApi(token, entry.id, {
        accountId,
        paidAt,
        amount: value,
        method,
        notes: notes.trim() || null,
        assetId: uploaded?.assetId ?? null,
        fileName: uploaded?.fileName ?? null,
      });
      toast.success(updated.status === "PAGO" ? (isReceivable ? "Recebido." : "Pago.") : "Baixa parcial registrada.");
      onSaved(updated);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao registrar a baixa.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal" role="dialog" aria-modal="true" aria-label={`${verb} título`}>
        <header className="dpModal__header">
          <h2 className="dpModal__title">{isReceivable ? "Registrar recebimento" : "Registrar pagamento"}</h2>
          <button type="button" className="dpModal__close" onClick={onClose} disabled={saving} aria-label="Fechar">
            &times;
          </button>
        </header>

        <form onSubmit={save}>
          <div className="dpModal__body">
            <p className="dpNotice" style={{ margin: 0 }}>
              <strong>{entry.description}</strong>
              {entry.installmentNumber ? ` (${entry.installmentNumber}/${entry.installmentCount})` : ""} · vence{" "}
              {formatDateOnly(entry.dueDate)} · em aberto <strong>{formatCurrency(entry.openAmount)}</strong>
            </p>

            {accounts && accounts.length === 0 ? (
              <div className="dpNotice dpNotice--warning">
                Nenhuma conta bancária ativa. Cadastre uma em Financeiro → Cadastros → Contas bancárias.
              </div>
            ) : null}

            <div className="dpFormGrid">
              <div className="dpFormGroup">
                <label htmlFor="stAccount">Conta *</label>
                <select
                  id="stAccount"
                  className="dpSelect"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={saving || !accounts}
                >
                  <option value="">{accounts ? "Escolha a conta" : "Carregando..."}</option>
                  {(accounts ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dpFormGroup">
                <label htmlFor="stDate">{isReceivable ? "Recebido em *" : "Pago em *"}</label>
                <input
                  id="stDate"
                  type="date"
                  className="dpInput"
                  value={paidAt}
                  max={todayYmd()}
                  onChange={(e) => setPaidAt(e.target.value)}
                  disabled={saving}
                />
              </div>

              <div className="dpFormGroup">
                <label htmlFor="stAmount">Valor (R$) *</label>
                <input
                  id="stAmount"
                  className="dpInput"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={saving}
                />
                {value > 0 && value < open ? (
                  <span className="dpFormHint">
                    Baixa parcial: ficam {formatCurrency(Math.round((open - value) * 100) / 100)} em aberto.
                  </span>
                ) : null}
              </div>

              <div className="dpFormGroup">
                <label htmlFor="stMethod">Forma *</label>
                <select
                  id="stMethod"
                  className="dpSelect"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as FinPaymentMethod)}
                  disabled={saving}
                >
                  {METHODS.map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="dpFormGroup">
              <label htmlFor="stFile">Comprovante (PDF ou imagem, até 10 MB)</label>
              <input
                id="stFile"
                type="file"
                className="dpInput"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={saving}
              />
            </div>

            <div className="dpFormGroup">
              <label htmlFor="stNotes">Observação</label>
              <textarea
                id="stNotes"
                className="dpTextarea"
                maxLength={500}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <footer className="dpModal__footer">
            <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="dpBtn dpBtn--primary" disabled={saving || errors.length > 0}>
              {saving ? "Salvando..." : isReceivable ? "Registrar recebimento" : "Registrar pagamento"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
